import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Navigation
    dashboard: 'Tableau de bord',
    members: 'Membres',
    subscriptions: 'Abonnements',
    equipment: 'Équipements',
    payments: 'Paiements',
    sessions: 'Séances',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    
    // Common
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    search: 'Rechercher',
    filter: 'Filtrer',
    export: 'Exporter',
    import: 'Importer',
    loading: 'Chargement...',
    
    // Auth
    login: 'Se connecter',
    email: 'Email',
    password: 'Mot de passe',
    rememberMe: 'Se souvenir de moi',
    forgotPassword: 'Mot de passe oublié ?',
    
    // Members
    memberName: 'Nom du membre',
    memberCode: 'Code membre',
    phone: 'Téléphone',
    address: 'Adresse',
    birthDate: 'Date de naissance',
    joinDate: 'Date d\'inscription',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    
    // Subscriptions
    subscriptionType: 'Type d\'abonnement',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    price: 'Prix',
    duration: 'Durée',
    
    // Equipment
    equipmentName: 'Nom de l\'équipement',
    equipmentType: 'Type d\'équipement',
    maintenanceDate: 'Date de maintenance',
    condition: 'État',
    
    // Payments
    amount: 'Montant',
    paymentMethod: 'Méthode de paiement',
    paymentDate: 'Date de paiement',
    invoice: 'Facture',
    
    // Dashboard
    totalMembers: 'Total des membres',
    activeMembers: 'Membres actifs',
    monthlyRevenue: 'Revenus mensuels',
    equipmentStatus: 'État des équipements',
  },
  ar: {
    // Navigation
    dashboard: 'لوحة القيادة',
    members: 'الأعضاء',
    subscriptions: 'الاشتراكات',
    equipment: 'المعدات',
    payments: 'المدفوعات',
    sessions: 'الجلسات',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    
    // Common
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    search: 'بحث',
    filter: 'تصفية',
    export: 'تصدير',
    import: 'استيراد',
    loading: 'جاري التحميل...',
    
    // Auth
    login: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور؟',
    
    // Members
    memberName: 'اسم العضو',
    memberCode: 'رمز العضو',
    phone: 'الهاتف',
    address: 'العنوان',
    birthDate: 'تاريخ الميلاد',
    joinDate: 'تاريخ الانضمام',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    
    // Subscriptions
    subscriptionType: 'نوع الاشتراك',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ الانتهاء',
    price: 'السعر',
    duration: 'المدة',
    
    // Equipment
    equipmentName: 'اسم المعدة',
    equipmentType: 'نوع المعدة',
    maintenanceDate: 'تاريخ الصيانة',
    condition: 'الحالة',
    
    // Payments
    amount: 'المبلغ',
    paymentMethod: 'طريقة الدفع',
    paymentDate: 'تاريخ الدفع',
    invoice: 'الفاتورة',
    
    // Dashboard
    totalMembers: 'إجمالي الأعضاء',
    activeMembers: 'الأعضاء النشطون',
    monthlyRevenue: 'الإيرادات الشهرية',
    equipmentStatus: 'حالة المعدات',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');
  const [isLoading, setIsLoading] = useState(true);

  // Load language preference from API
  useEffect(() => {
    const fetchLanguagePreference = async () => {
      try {
        const response = await axios.get('/api/settings/language');
        if (response.data && response.data.language) {
          setLanguage(response.data.language);
        }
      } catch (error) {
        console.error('Error fetching language preference:', error);
        // Default to French if API call fails
        setLanguage('fr');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLanguagePreference();
  }, []);

  // Update language preference through API
  useEffect(() => {
    if (!isLoading) {
      const saveLanguagePreference = async () => {
        try {
          await axios.post('/api/settings/language', { language });
        } catch (error) {
          console.error('Error saving language preference:', error);
        }
      };

      saveLanguagePreference();
    }

    // Update document attributes
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language, isLoading]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    isRTL: language === 'ar',
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}; 